'use client';

import { DELIVERY_TYPE, ORDER_STATUS } from '@/lib/Constants';
import { Endpoints } from '@/lib/Endpoints';
import { ErrorHandler } from '@/lib/ErrorHandler';
import { Requester } from '@/lib/Requester';
import { formatNumberToPriceText } from '@/lib/utils';
import { GenericResponse } from '@/models/GenericResponse';
import {
  Order,
  OrderProduct,
  OrderProductMessage,
  OrderProductNote,
  OrderResponse,
} from '@/models/Order';
import { PRODUCT_TYPE, type Product } from '@/models/Product';
import {
  Camera,
  Hash,
  MapPin,
  MessageSquareText,
  NotebookPen,
  Package,
  Pencil,
} from 'lucide-react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { OrderStatus } from './OrderHistoryView';
import type { PhotoPrintEditorResult } from './photo-print/PhotoPrintEditor';
import SmartImage from './SmartImage';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { useToast } from './ui/use-toast';
import VideoMessageViewerDialog from './VideoMessageViewerDialog';

const PhotoPrintEditor = dynamic(() => import('./photo-print/PhotoPrintEditor'), {
  ssr: false,
});

function hasLegacyMessageContent(message: OrderProductMessage) {
  return Boolean(message.content || message.sprayText1 || message.sprayText2);
}

function isPhotoPrintItem(item: OrderProduct) {
  return (
    Number(item.productTypeId) === PRODUCT_TYPE.PHOTO_PRINT ||
    Boolean(item.photoPrintInformation?.imageUrl)
  );
}

function isExtraItem(item: OrderProduct) {
  return Number(item.isExtra) === 1 || item.isExtra === true;
}

function getProductNotes(item: OrderProduct): Array<{
  content?: string | null;
  senderName?: string | null;
  sprayText?: string | null;
  sprayText1?: string | null;
  sprayText2?: string | null;
  messageId?: number | null;
  isAnonymous?: number;
}> {
  if (Array.isArray(item.productNotes) && item.productNotes.length > 0) {
    return item.productNotes
      .map((note: OrderProductNote) => ({
        content: note.content || null,
        senderName: note.senderName || note.senderTitle || null,
        sprayText:
          [note.sprayText1, note.sprayText2].filter(Boolean).join(' / ') ||
          (typeof note.spray === 'string' ? note.spray : null),
        sprayText1: note.sprayText1 || null,
        sprayText2: note.sprayText2 || null,
        messageId: note.messageId ?? null,
        isAnonymous: Number(note.isAnonymous) || 0,
      }))
      .filter((note) => Boolean(note.content || note.sprayText));
  }

  return (item.messageInformation || []).filter(hasLegacyMessageContent).map((message) => ({
    content: message.content || null,
    senderName: message.senderTitle || null,
    sprayText: [message.sprayText1, message.sprayText2].filter(Boolean).join(' / ') || null,
    sprayText1: message.sprayText1 || null,
    sprayText2: message.sprayText2 || null,
    messageId: message.messageId,
    isAnonymous: Number(message.isAnonymous) || 0,
  }));
}

type EditSection = 'orderNote' | 'video' | `note-${number}` | null;

function buildPhotoPrintProductFromOrderItem(item: OrderProduct): Product {
  return {
    id: item.productId || item.id,
    inStock: 1,
    code: '',
    title: item.title,
    titleWD: item.title,
    imageUrl: item.imageUrl,
    url: item.url || '',
    price: item.price || 0,
    images: [],
    isExtra: true,
    productTypeId: PRODUCT_TYPE.PHOTO_PRINT,
    showCaptionDialog: false,
  };
}

export function OrderDetailView({
  order,
  onOrderUpdated,
}: {
  order: Order;
  onOrderUpdated?: (order: Order) => void;
}) {
  const { toast } = useToast();
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [editSection, setEditSection] = useState<EditSection>(null);
  const [saving, setSaving] = useState(false);
  const [orderNoteDraft, setOrderNoteDraft] = useState('');
  const [noteDrafts, setNoteDrafts] = useState<
    Record<
      number,
      {
        content: string;
        senderTitle: string;
        sprayText1: string;
        sprayText2: string;
        messageId?: number | null;
        isAnonymous: number;
      }
    >
  >({});
  const [videoDraftKey, setVideoDraftKey] = useState<string | null>(null);
  const [photoEditItem, setPhotoEditItem] = useState<OrderProduct | null>(null);
  const [photoEditSubmitting, setPhotoEditSubmitting] = useState(false);

  const canEdit = Number(order.orderStatus) < ORDER_STATUS.PRINTED;
  const isDecorationOrder = Number(order.deliveryType) === DELIVERY_TYPE.DECORATION;
  const depotLines = [order.district?.title, order.district?.locationData?.address].filter(
    (line): line is string => Boolean(line?.trim()),
  );
  const deliveryLocationLabel = isDecorationOrder ? 'Depo' : 'Teslimat adresi';
  const deliveryLocationText = isDecorationOrder
    ? depotLines.join('\n') || '—'
    : order.receiverInformation?.address || '—';
  const receiverTitle = order.receiverInformation?.title;
  const hasVideoMessage = Boolean(order.hasVideoMessage || order.videoMessageKey);
  const deliveryPhotos = (order.deliveryPhotos || []).filter((photo) => Boolean(photo.url));

  async function saveCustomerContent(body: Record<string, unknown> | FormData) {
    setSaving(true);
    try {
      const { data: result } = await Requester.post<GenericResponse<OrderResponse>>(
        Endpoints.UPDATE_ORDER_CUSTOMER_CONTENT,
        body,
      );
      const updated = result.data.order;
      onOrderUpdated?.(updated);
      setEditSection(null);
      toast({ description: 'Değişiklikler kaydedildi' });
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setSaving(false);
    }
  }

  function startEditOrderNote() {
    setOrderNoteDraft(order.orderNote || '');
    setEditSection('orderNote');
  }

  function startEditProductNote(item: OrderProduct, itemIndex: number) {
    const notes = getProductNotes(item);
    const first = notes[0];
    setNoteDrafts((prev) => ({
      ...prev,
      [itemIndex]: {
        content: first?.content || '',
        senderTitle: first?.senderName || '',
        sprayText1: first?.sprayText1 || '',
        sprayText2: first?.sprayText2 || '',
        messageId: first?.messageId ?? null,
        isAnonymous: first?.isAnonymous ?? 0,
      },
    }));
    setEditSection(`note-${itemIndex}`);
  }

  const closePhotoEditor = useCallback(() => {
    if (photoEditSubmitting) return;
    setPhotoEditItem(null);
  }, [photoEditSubmitting]);

  const handlePhotoEditorComplete = useCallback(
    async (result: PhotoPrintEditorResult) => {
      if (!photoEditItem) return;

      setPhotoEditSubmitting(true);
      try {
        const formData = new FormData();
        formData.append('orderId', String(order.orderId));
        formData.append('productId', String(photoEditItem.productId || photoEditItem.id));
        if (photoEditItem.basketItemId) {
          formData.append('basketItemId', String(photoEditItem.basketItemId));
        }
        formData.append('file', result.file);

        const { data: apiResult } = await Requester.post<GenericResponse<OrderResponse>>(
          Endpoints.UPDATE_ORDER_CUSTOMER_CONTENT,
          formData,
        );
        onOrderUpdated?.(apiResult.data.order);
        setPhotoEditItem(null);
        toast({ description: 'Fotoğraf baskısı güncellendi' });
      } catch (error) {
        const { message } = ErrorHandler.getMessage(error);
        toast({
          variant: 'destructive',
          description: message,
        });
      } finally {
        setPhotoEditSubmitting(false);
      }
    },
    [onOrderUpdated, order.orderId, photoEditItem, toast],
  );

  return (
    <>
      <div className="flex flex-col gap-5">
        <section className="rounded-2xl bg-white border border-hzc_beige_300 overflow-hidden">
          <div className="bg-hzc_purple_500 text-white px-4 py-4 sm:px-5">
            <div className="flex flex-col gap-3 sm:hidden">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] text-white/70">Sipariş numarası</p>
                  <p className="text-base font-semibold break-all">#{order.orderNumber}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[11px] text-white/70">Toplam</p>
                  <p className="text-base font-bold">
                    {formatNumberToPriceText(order.total || 0)} TL
                  </p>
                </div>
              </div>
              <div className="w-fit rounded-full bg-white/15 px-3 py-1.5 [&_span]:text-white">
                <OrderStatus status={order.orderStatus} />
              </div>
            </div>

            <div className="hidden sm:flex sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2 min-w-0">
                <Hash className="size-5 shrink-0 opacity-80" />
                <div className="min-w-0">
                  <p className="text-xs text-white/70">Sipariş numarası</p>
                  <p className="text-lg font-semibold truncate">#{order.orderNumber}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="rounded-full bg-white/15 px-3 py-1.5 [&_span]:text-white">
                  <OrderStatus status={order.orderStatus} />
                </div>
                <div className="text-right">
                  <p className="text-xs text-white/70">Toplam</p>
                  <p className="text-lg font-bold">
                    {formatNumberToPriceText(order.total || 0)} TL
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 py-4 sm:px-5 flex gap-3">
            <div className="mt-0.5 flex size-8 sm:size-9 shrink-0 items-center justify-center rounded-full bg-hzc_purple_500/10">
              <MapPin className="size-4 text-hzc_purple_500" />
            </div>
            <div className="min-w-0 flex flex-col gap-1">
              <span className="text-sm font-semibold text-hzc_beige_900">
                {deliveryLocationLabel}
              </span>
              {receiverTitle && !isDecorationOrder && (
                <span className="text-sm text-hzc_beige_900">{receiverTitle}</span>
              )}
              <span className="text-sm text-hzc_beige_700 whitespace-pre-line leading-relaxed break-words">
                {deliveryLocationText}
              </span>
            </div>
          </div>

          {(order.orderNote || canEdit) && (
            <div className="px-4 pb-4 sm:px-5 flex gap-3 border-t border-hzc_beige_200 pt-4">
              <div className="mt-0.5 flex size-8 sm:size-9 shrink-0 items-center justify-center rounded-full bg-hzc_purple_500/10">
                <NotebookPen className="size-4 text-hzc_purple_500" />
              </div>
              <div className="min-w-0 flex-1 flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-hzc_beige_900">
                    Sipariş Hazırlanma Notu
                  </span>
                  {canEdit && editSection !== 'orderNote' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-hzc_purple_500"
                      onClick={startEditOrderNote}
                    >
                      <Pencil className="size-3.5 mr-1" />
                      Düzenle
                    </Button>
                  )}
                </div>
                {editSection === 'orderNote' ? (
                  <div className="flex flex-col gap-2">
                    <textarea
                      value={orderNoteDraft}
                      onChange={(e) => setOrderNoteDraft(e.target.value)}
                      maxLength={500}
                      rows={3}
                      className="w-full rounded-xl border border-hzc_beige_300 bg-white px-3 py-2 text-sm text-hzc_beige_900"
                      placeholder="Sipariş Hazırlanma Notu ekleyin"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        disabled={saving}
                        className="rounded-full bg-hzc_purple_500 text-white"
                        onClick={() =>
                          saveCustomerContent({
                            orderId: order.orderId,
                            orderNote: orderNoteDraft,
                          })
                        }
                      >
                        Kaydet
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={saving}
                        onClick={() => setEditSection(null)}
                      >
                        İptal
                      </Button>
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-hzc_beige_700 whitespace-pre-line leading-relaxed break-words">
                    {order.orderNote || '—'}
                  </span>
                )}
              </div>
            </div>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2 px-1">
            <div className="flex size-8 items-center justify-center rounded-full bg-hzc_purple_500/10">
              <Package className="size-4 text-hzc_purple_500" />
            </div>
            <h2 className="text-base font-semibold text-hzc_beige_900">
              Ürünler
              <span className="ml-2 text-sm font-normal text-hzc_beige_700">
                ({order.items?.length || 0})
              </span>
            </h2>
          </div>

          <div className="flex flex-col gap-3">
            {order.items.map((item, index) => {
              const isPhotoPrint = isPhotoPrintItem(item);
              const isExtra = isExtraItem(item);
              const canEditProductNote = canEdit && !isPhotoPrint && !isExtra;
              const notes = isPhotoPrint || isExtra ? [] : getProductNotes(item);
              const photoPrint = item.photoPrintInformation;
              const photoUrl = photoPrint?.imageUrl;
              const noteSection = `note-${index}` as const;

              return (
                <article
                  key={`${item.id}-${index}`}
                  className="rounded-2xl border border-hzc_beige_300 bg-white p-4 sm:p-5 flex min-w-0 flex-col gap-4 overflow-hidden"
                >
                  <div className="flex flex-row gap-4 items-start">
                    <Link
                      href={item.url || ''}
                      className="shrink-0 overflow-hidden rounded-xl bg-hzc_beige_100"
                    >
                      <SmartImage
                        src={item.imageUrl}
                        alt={`${item.title} - Ürün Görseli`}
                        className="aspect-square object-cover"
                        width={88}
                        height={88}
                        imageType="icon"
                      />
                    </Link>
                    <div className="min-w-0 flex-1 flex flex-col gap-2">
                      <Link
                        href={item.url || ''}
                        className="text-base font-medium text-hzc_beige_900 hover:text-hzc_purple_500 transition-colors line-clamp-2"
                      >
                        {item.title}
                      </Link>
                      <div className="flex flex-row flex-wrap items-center gap-2">
                        <span className="rounded-full bg-hzc_beige_100 px-2.5 py-1 text-xs text-hzc_beige_700">
                          Adet {item.count}
                        </span>
                        <span className="text-sm font-semibold text-hzc_purple_500">
                          {formatNumberToPriceText(item?.price || 0)} TL
                        </span>
                      </div>
                    </div>
                  </div>

                  {(photoUrl || (canEdit && photoPrint)) && (
                    <div className="rounded-xl border border-hzc_beige_300 bg-hzc_beige_100/80 p-4 flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-hzc_beige_700">
                          <Camera className="size-3.5" />
                          Fotoğraf Baskısı
                        </span>
                        {canEdit && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-hzc_purple_500"
                            onClick={() => setPhotoEditItem(item)}
                          >
                            <Pencil className="size-3.5 mr-1" />
                            Düzenle
                          </Button>
                        )}
                      </div>
                      {photoUrl && (
                        <a
                          href={photoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full max-w-[280px] overflow-hidden rounded-xl bg-white"
                        >
                          <SmartImage
                            src={photoUrl}
                            alt={`${item.title} - Fotoğraf baskı`}
                            className="aspect-square w-full object-cover"
                            width={280}
                            height={280}
                            imageType="icon"
                          />
                        </a>
                      )}
                    </div>
                  )}

                  {!isPhotoPrint && !isExtra && (notes.length > 0 || canEditProductNote) && (
                    <div className="rounded-xl border border-dashed border-hzc_beige_400 bg-hzc_beige_100/60 p-3 flex flex-col gap-3 overflow-hidden min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-hzc_beige_700">
                          <MessageSquareText className="size-3.5" />
                          Ürün notu
                        </span>
                        {canEditProductNote && editSection !== noteSection && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-hzc_purple_500"
                            onClick={() => startEditProductNote(item, index)}
                          >
                            <Pencil className="size-3.5 mr-1" />
                            Düzenle
                          </Button>
                        )}
                      </div>

                      {editSection === noteSection ? (
                        <div className="flex flex-col gap-2">
                          <textarea
                            value={noteDrafts[index]?.content || ''}
                            onChange={(e) =>
                              setNoteDrafts((prev) => ({
                                ...prev,
                                [index]: {
                                  ...prev[index],
                                  content: e.target.value,
                                },
                              }))
                            }
                            rows={3}
                            maxLength={500}
                            placeholder="Ürün notu"
                            className="w-full rounded-xl border border-hzc_beige_300 bg-white px-3 py-2 text-sm"
                          />
                          <span className="text-xs text-hzc_beige_700 text-right">
                            {(noteDrafts[index]?.content || '').length} / 500
                          </span>
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
                            <div className="min-w-0 flex-1 flex flex-col gap-1">
                              <span className="text-xs text-hzc_beige_700">
                                Adınız (isteğe bağlı)
                              </span>
                              <input
                                type="text"
                                value={noteDrafts[index]?.senderTitle || ''}
                                disabled={Number(noteDrafts[index]?.isAnonymous) === 1}
                                onChange={(e) =>
                                  setNoteDrafts((prev) => ({
                                    ...prev,
                                    [index]: {
                                      ...prev[index],
                                      senderTitle: e.target.value,
                                    },
                                  }))
                                }
                                placeholder="Ad Soyad"
                                className="w-full rounded-xl border border-hzc_beige_300 bg-white px-3 py-2 text-sm disabled:bg-hzc_beige_100 disabled:text-hzc_beige_600"
                              />
                            </div>
                            <label className="flex items-center gap-2 pb-2 shrink-0 cursor-pointer">
                              <Checkbox
                                className="size-6"
                                checked={Number(noteDrafts[index]?.isAnonymous) === 1}
                                onCheckedChange={(isChecked) =>
                                  setNoteDrafts((prev) => ({
                                    ...prev,
                                    [index]: {
                                      ...prev[index],
                                      isAnonymous: isChecked ? 1 : 0,
                                      senderTitle: isChecked ? '' : prev[index]?.senderTitle || '',
                                    },
                                  }))
                                }
                              />
                              <span className="text-sm text-hzc_beige_700">İsimsiz Gönder</span>
                            </label>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              disabled={saving}
                              className="rounded-full bg-hzc_purple_500 text-white"
                              onClick={() => {
                                const draft = noteDrafts[index];
                                const isAnonymous = Number(draft?.isAnonymous) === 1 ? 1 : 0;
                                return saveCustomerContent({
                                  orderId: order.orderId,
                                  messages: [
                                    {
                                      productId: item.productId || item.id,
                                      messageId: draft?.messageId ?? null,
                                      content: draft?.content || '',
                                      isAnonymous,
                                      senderTitle: isAnonymous ? '' : draft?.senderTitle || '',
                                      sprayText1: draft?.sprayText1 || '',
                                      sprayText2: draft?.sprayText2 || '',
                                    },
                                  ],
                                });
                              }}
                            >
                              Kaydet
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              disabled={saving}
                              onClick={() => setEditSection(null)}
                            >
                              İptal
                            </Button>
                          </div>
                        </div>
                      ) : (
                        notes.map((note, noteIndex) => (
                          <div
                            key={`${item.id}-note-${noteIndex}`}
                            className="flex min-w-0 flex-col gap-1 border-t border-hzc_beige_300/70 pt-3 first:border-t-0 first:pt-0"
                          >
                            {note.content && (
                              <span className="text-sm text-hzc_beige_900 whitespace-pre-line break-words break-all leading-relaxed">
                                {note.content}
                              </span>
                            )}
                            {note.sprayText && (
                              <span className="text-sm text-hzc_beige_900 break-words break-all">
                                {note.sprayText}
                              </span>
                            )}
                          </div>
                        ))
                      )}
                      {notes.length === 0 && editSection !== noteSection && (
                        <span className="text-sm text-hzc_beige_700">—</span>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      </div>

      {hasVideoMessage && order.videoMessageKey && (
        <VideoMessageViewerDialog
          open={videoDialogOpen}
          onOpenChange={setVideoDialogOpen}
          videoKey={order.videoMessageKey}
        />
      )}

      {photoEditItem &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[100] overflow-y-auto bg-hzc_beige_200">
            <PhotoPrintEditor
              selectedProduct={buildPhotoPrintProductFromOrderItem(photoEditItem)}
              currentImageUrl={photoEditItem.photoPrintInformation?.imageUrl || null}
              showCaptionDialog={false}
              quantity={photoEditItem.count || 1}
              confirmLabel="Onayla ve fotoğrafı güncelle"
              onCancel={closePhotoEditor}
              onComplete={handlePhotoEditorComplete}
              isSubmitting={photoEditSubmitting}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
